import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { Booking } from '../../types';
import type { BookingLine, BookingMoneyTotals } from '../../pricing/pricingEngine';
import { getPaymentMethodLabel } from './reservationsAdmin.utils';

type Props = {
  booking: Booking;
};

type BookingWithPricingSnapshot = Booking & {
  bookingLines?: BookingLine[] | null;
  moneyTotals?: Partial<BookingMoneyTotals> | null;
};

function formatEuro(amount: number) {
  return `${Math.round((amount + Number.EPSILON) * 100) / 100} EUR`;
}

function getBookingMoneySummary(booking: BookingWithPricingSnapshot) {
  const moneyTotals = booking.moneyTotals;
  if (!moneyTotals) {
    return null;
  }

  const initialTotal = Number(moneyTotals.initialTotal);
  const currentTotalDue = Number(moneyTotals.currentTotalDue);
  const totalPaid = Number(moneyTotals.totalPaid);
  const totalOutstanding = Number(moneyTotals.totalOutstanding);
  const totalRefundable = Number(moneyTotals.totalRefundable);

  if (
    !Number.isFinite(initialTotal)
    || !Number.isFinite(currentTotalDue)
    || !Number.isFinite(totalPaid)
    || !Number.isFinite(totalOutstanding)
    || !Number.isFinite(totalRefundable)
  ) {
    return null;
  }

  return {
    initialTotal: Math.max(0, initialTotal),
    currentTotalDue: Math.max(0, currentTotalDue),
    totalPaid: Math.max(0, totalPaid),
    totalOutstanding: Math.max(0, totalOutstanding),
    totalRefundable: Math.max(0, totalRefundable),
  };
}

export default function ReservationPaymentBlock({ booking }: Props) {
  const paymentMethodLabel = getPaymentMethodLabel(booking.paymentMethod);
  const bookingSnapshot = booking as BookingWithPricingSnapshot;
  const moneySummary = getBookingMoneySummary(bookingSnapshot);
  const partialRefundAmount = moneySummary?.totalRefundable ?? 0;
  const fallbackInitial = Number(booking.amount);
  const fallbackDue = Number(booking.amountDueAfterPartialCancellation ?? booking.amount);
  const fallbackPaid = Number(booking.paymentValidatedAmount);

  const displayedMoneySummary = moneySummary ?? (
    Number.isFinite(fallbackInitial)
      ? {
          initialTotal: Math.max(0, fallbackInitial),
          currentTotalDue: Number.isFinite(fallbackDue) ? Math.max(0, fallbackDue) : Math.max(0, fallbackInitial),
          totalPaid: Number.isFinite(fallbackPaid) ? Math.max(0, fallbackPaid) : 0,
          totalOutstanding: Number.isFinite(fallbackDue)
            ? Math.max(0, Math.max(0, fallbackDue) - (Number.isFinite(fallbackPaid) ? Math.max(0, fallbackPaid) : 0))
            : Math.max(0, fallbackInitial - (Number.isFinite(fallbackPaid) ? Math.max(0, fallbackPaid) : 0)),
          totalRefundable: Math.max(0, Number(booking.partialRefund?.amount ?? booking.refundAmountRequested ?? booking.refundAmountProcessed ?? 0)),
        }
      : null
  );

  function KeyValueRow({
    label,
    value,
    emphasized = false,
  }: {
    label: string;
    value: string;
    emphasized?: boolean;
  }) {
    return (
      <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: emphasized ? 700 : 600,
            textAlign: 'right',
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em' }}>
          Paiement
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
          Synthèse des montants sans empilement de badges.
        </Typography>
      </Box>

      <Stack spacing={0.75}>
        <KeyValueRow label="Méthode" value={paymentMethodLabel} emphasized />
        <KeyValueRow label="Initial" value={displayedMoneySummary ? formatEuro(displayedMoneySummary.initialTotal) : 'Non disponible'} />
        <KeyValueRow label="Payé" value={displayedMoneySummary ? formatEuro(displayedMoneySummary.totalPaid) : 'Non disponible'} />
        <KeyValueRow label="Restant" value={displayedMoneySummary ? formatEuro(displayedMoneySummary.totalOutstanding) : 'Non disponible'} />
        <KeyValueRow label="Montant courant" value={displayedMoneySummary ? formatEuro(displayedMoneySummary.currentTotalDue) : 'Non disponible'} />
        <KeyValueRow label="Remboursable" value={displayedMoneySummary ? formatEuro(displayedMoneySummary.totalRefundable) : formatEuro(partialRefundAmount)} />
      </Stack>

      {booking.paymentMethod === 'bank_transfer' && booking.bankTransferCommunication ? (
        <Tooltip title={booking.bankTransferCommunication}>
          <Box
            sx={{
              minWidth: 0,
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(192, 110, 82, 0.06)',
              border: '1px solid rgba(192, 110, 82, 0.16)',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', color: 'secondary.main', fontWeight: 700 }}>
              Communication virement
            </Typography>
            <Typography variant="body2" sx={{ minWidth: 0, fontWeight: 600, color: 'text.primary', overflowWrap: 'anywhere' }}>
              {booking.bankTransferCommunication}
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Aucun complément de paiement à afficher.
        </Typography>
      )}
    </Stack>
  );
}
